/**
 * Company: SPHMedia
 * Description: [Lists Kaki Makan Restaurants]
 */

import useUserVarStore from "@app/store/useUserVarStore";
import RestaurantCard from "@components/Blocks/Card/Restaurant/RestaurantCard";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import LoadMore, { LoadingGif } from "@components/Modules/LoadMore/LoadMore";
import {
  KAKI_MAKAN_ADS_SECTION,
  KAKI_MAKAN_LANDING_PAGE,
  KAKI_MAKAN_REF_CODE_READ_ARTICLE,
  LOAD_MORE_COPY,
} from "@config/kakiMakan";
import { AdsLb2 } from "@elements/Advertisement/variant/AdsLb";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import { displayAfter } from "@helper/utils";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import useGoogleRatings from "@hooks/useGoogleRatings";
import useWindowDimensions from "@hooks/useWindowDimensions";
import { NoSSR } from "@sphtech/web2-core/components";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import {
  GooglePlaceDetails,
  GoogleRatingResponse,
} from "@typings/GooglePlaces";
import React from "react";
import { type ReactElement, useMemo, useState } from "react";

import useLoadMore from "../hooks/useLoadMore";
import useReactionList from "../hooks/useReactionList";
import { KakiMakanSearchFilter } from "./KakiMakanSearchFilter";

type KakiMakanRestaurantsProps = {
  kakiMakanArticles: ProcessedArticleData[];
  initialRatings: Record<string, GoogleRatingResponse>;
};

const PAGE_SIZE = 8;
const INITIAL_RESTAURANT_COUNT = 8;

/**
 * KakiMakanRestaurants
 * Description: [Lists Kaki Makan Restaurants with search and filter functionalities]
 *
 * Parent component combining:
 * - Search/Filter (via KakiMakanSearchFilter + useKakiMakanSearchAndFilter)
 * - Load More (via useLoadMore)
 *
 * Flow:
 * 1. User types search/filter → triggers `onQueryChange`.
 * 2. Parent updates `searchQuery` & `filterQuery`.
 * 3. useLoadMore refetches/loads more data based on updated queries.
 *
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function KakiMakanRestaurants({
  kakiMakanArticles,
  initialRatings,
}: KakiMakanRestaurantsProps): ReactElement {
  const { userToken } = useUserVarStore();
  const [restaurants, setRestaurants] =
    useState<ProcessedArticleData[]>(kakiMakanArticles);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filterQuery, setFilterQuery] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { width } = useWindowDimensions();
  const isDesktop = useMemo(() => width >= 992, [width]);

  const pageAdTarget = PageAdTargetingTypeEnum.LISTING;

  const {
    loadMoreData,
    showLoadMore,
    handleLoadMore,
    hasMoreStories,
    pageNumber,
  } = useLoadMore({
    kakiMakanArticles: restaurants,
    initial_restaurant_count: INITIAL_RESTAURANT_COUNT,
    page_size: PAGE_SIZE,
    searchQuery,
    filterQuery,
  });

  const articles = useMemo(
    () => [...restaurants, ...loadMoreData],
    [restaurants, loadMoreData],
  );

  const articleIds = useMemo(() => articles.map((a) => a.id), [articles]);
  const { data: reactionListData } = useReactionList({ articleIds });

  const ratingItems = useMemo(
    () =>
      articles.map((a) => ({
        restaurantName: a.location?.title ?? "",
        articleId: a.id,
      })),
    [articles],
  );
  const { data: googleRatings } = useGoogleRatings({
    items: ratingItems,
    initialData: initialRatings,
  });

  const isValidRating = (
    val: GoogleRatingResponse | undefined,
  ): val is { id: string; data: GooglePlaceDetails } => {
    return !!val && !val.error && val.data !== null;
  };

  return (
    <>
      <div className="flex flex-col w-full gap-2">
        <KakiMakanSearchFilter
          onDataFetched={setRestaurants}
          onLoading={setIsLoading}
          onReset={() => {
            setRestaurants(kakiMakanArticles);
          }}
          onQueryChange={(search, filter) => {
            setSearchQuery(search);
            setFilterQuery(filter);
          }}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 justify-items-center lg:justify-start items-start">
        {isLoading ? (
          <LoadingGif />
        ) : restaurants.length === 0 ? (
          <p className="text-center col-span-full">No results found.</p>
        ) : (
          articles.map((article, index) => {
            const item = googleRatings[article.id];
            const rating = isValidRating(item) ? item.data.rating : undefined;

            return (
              <React.Fragment key={index}>
                <NoSSR>
                  {displayAfter(index, PAGE_SIZE) && isDesktop && (
                    <AdsContainer
                      key={`desktop-ad-${index}`}
                      className="hidden lg:block col-span-full"
                    >
                      <CompanionAdsLb
                        index={index / PAGE_SIZE}
                        uniqueSectionName={KAKI_MAKAN_ADS_SECTION}
                        stripeBackground={false}
                        pageAdTargetType={pageAdTarget}
                        applySizeMapping
                      />
                    </AdsContainer>
                  )}
                  {displayAfter(index, 4) && !isDesktop && (
                    <AdsContainer
                      key={`mobile-ad-${index}`}
                      className="flex col-span-full lg:hidden"
                    >
                      <CompanionAdsLb
                        index={index / 4}
                        uniqueSectionName={KAKI_MAKAN_ADS_SECTION}
                        stripeBackground={false}
                        pageAdTargetType={pageAdTarget}
                        applySizeMapping
                      />
                    </AdsContainer>
                  )}
                </NoSSR>
                <RestaurantCard
                  key={article.id}
                  articleId={article.id}
                  title={article.location?.title || ""}
                  imageField={article.imageField}
                  imageAlt={article.imageAlt}
                  address={article.location?.address || ""}
                  articleUrl={
                    article.urlPath
                      ? `${article.urlPath}${KAKI_MAKAN_REF_CODE_READ_ARTICLE}`
                      : ""
                  }
                  reactions={reactionListData?.[article.id]?.reactions}
                  reactionSetId={reactionListData?.[article.id]?.set}
                  publishedDate={article.datetime}
                  userToken={userToken}
                  expandedId={expandedId}
                  onExpand={setExpandedId}
                  rating={rating}
                />
              </React.Fragment>
            );
          })
        )}
      </div>
      <AdsContainer>
        <AdsLb2
          uniqueSectionName={KAKI_MAKAN_ADS_SECTION}
          pageAdTargetType={pageAdTarget}
          hasStripeBg={false}
        />
      </AdsContainer>
      <div className="bg-neutral-300 h-[1px] w-full my-2.5"></div>
      {showLoadMore && (
        <LoadMore
          rootClassName="w-[166px] h-[52px] bg-brand-primary border-y-0 flex justify-center items-center my-0 px-8 py-8 text-[20px] text-text-inverse"
          buttonClass="capitalize leading-[120%]"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText={LOAD_MORE_COPY}
          virtualPVTitle={KAKI_MAKAN_LANDING_PAGE}
          pageNumber={pageNumber}
          showButton={true}
        />
      )}
    </>
  );
}
